/*
 * Wishlist account sync (cross-device) via Shopify's Customer Account API.
 *
 * Setup completed:
 *   1. custom.wishlist_test metafield: Customer Account API read/write enabled.
 *   2. Headless channel storefront "Big Frenchies Wishlist Sync" created,
 *      Client ID below, using this store's custom accounts domain
 *      (account.bigfrenchies.com) for the OAuth authorize/token endpoints.
 *
 * Still required in the Headless storefront's Customer Account API settings
 * (Application setup section) before this works end to end:
 *   - Callback URI(s): https://www.bigfrenchies.com/pages/wishlist
 *   - JavaScript origin(s): https://www.bigfrenchies.com
 *
 * How it works:
 *  - window.wishlistSyncLogin() sends the customer to Shopify's hosted login
 *    (account.bigfrenchies.com) using the OAuth Authorization Code + PKCE
 *    flow (no client secret needed - safe to run entirely in the browser).
 *  - On return, the code is exchanged for an access token, which is used to
 *    read/write a JSON metafield on the customer's own account holding their
 *    saved product handles.
 *  - The server list is merged with whatever is already saved locally, then
 *    both sides are updated so nothing is lost either way.
 *  - Tokens are cached in localStorage and refreshed silently on later visits
 *    so the customer doesn't have to log in again every time.
 */
(function () {
  // ---- Configuration -------------------------------------------------
  var SHOP_ID = '43479859361';
  var CLIENT_ID = 'ba44b621-3a87-4e49-882d-dcc94862b2ee';
  var API_VERSION = '2025-01';
  var REDIRECT_URI = 'https://www.bigfrenchies.com/pages/wishlist';
  var SCOPES = 'openid email customer-account-api:full';
  var METAFIELD_NAMESPACE = 'custom';
  var METAFIELD_KEY = 'wishlist_test'; // rename here if you rename the definition's key in admin

  // The account/OAuth endpoints live on this store's custom accounts domain.
  var AUTH_BASE = 'https://account.bigfrenchies.com/authentication';
  // The Customer Account API GraphQL endpoint is always the canonical
  // shopify.com/{shop_id} URL, regardless of the accounts domain above.
  var API_URL = 'https://shopify.com/' + SHOP_ID + '/account/customer/api/' + API_VERSION + '/graphql';

  var TOKEN_STORAGE_KEY = 'bf_wishlist_account_tokens';
  var VERIFIER_STORAGE_KEY = 'bf_wishlist_oauth_verifier';
  var STATE_STORAGE_KEY = 'bf_wishlist_oauth_state';

  function isConfigured() {
    return CLIENT_ID && CLIENT_ID.indexOf('YOUR_') !== 0;
  }

  // ---- PKCE helpers ----------------------------------------------------
  function randomString(length) {
    var chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    var out = '';
    var randomValues = new Uint8Array(length);
    crypto.getRandomValues(randomValues);
    for (var i = 0; i < length; i++) {
      out += chars[randomValues[i] % chars.length];
    }
    return out;
  }

  function base64UrlEncode(buffer) {
    var bytes = new Uint8Array(buffer);
    var binary = '';
    for (var i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  function sha256(text) {
    var encoder = new TextEncoder();
    var data = encoder.encode(text);
    return crypto.subtle.digest('SHA-256', data);
  }

  // ---- Token storage -----------------------------------------------------
  function getTokens() {
    try {
      var raw = localStorage.getItem(TOKEN_STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function saveTokens(tokens) {
    try {
      tokens.obtained_at = Date.now();
      localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(tokens));
    } catch (e) {}
  }

  function clearTokens() {
    try {
      localStorage.removeItem(TOKEN_STORAGE_KEY);
    } catch (e) {}
  }

  // ---- OAuth: start login -------------------------------------------------
  window.wishlistSyncLogin = function () {
    if (!isConfigured()) {
      console.warn('Wishlist account sync is not configured yet (missing Client ID).');
      return;
    }
    var verifier = randomString(64);
    var state = randomString(32);
    sessionStorage.setItem(VERIFIER_STORAGE_KEY, verifier);
    sessionStorage.setItem(STATE_STORAGE_KEY, state);

    sha256(verifier).then(function (digest) {
      var challenge = base64UrlEncode(digest);
      var params = new URLSearchParams({
        client_id: CLIENT_ID,
        response_type: 'code',
        redirect_uri: REDIRECT_URI,
        scope: SCOPES,
        state: state,
        code_challenge: challenge,
        code_challenge_method: 'S256'
      });
      window.location.href = AUTH_BASE + '/oauth/authorize?' + params.toString();
    });
  };

  // ---- OAuth: handle redirect back from Shopify ---------------------------
  function handleRedirectCallback() {
    var url = new URL(window.location.href);
    var code = url.searchParams.get('code');
    var state = url.searchParams.get('state');
    if (!code) return Promise.resolve(false);

    var expectedState = sessionStorage.getItem(STATE_STORAGE_KEY);
    var verifier = sessionStorage.getItem(VERIFIER_STORAGE_KEY);
    sessionStorage.removeItem(STATE_STORAGE_KEY);
    sessionStorage.removeItem(VERIFIER_STORAGE_KEY);

    if (!verifier || !state || state !== expectedState) {
      return Promise.resolve(false);
    }

    var body = new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: CLIENT_ID,
      redirect_uri: REDIRECT_URI,
      code: code,
      code_verifier: verifier
    });

    return fetch(AUTH_BASE + '/oauth/token', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString()
    })
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .then(function (tokens) {
        if (!tokens || !tokens.access_token) return false;
        saveTokens(tokens);
        // Clean the code/state out of the visible URL
        url.searchParams.delete('code');
        url.searchParams.delete('state');
        window.history.replaceState({}, document.title, url.pathname + url.search);
        return true;
      })
      .catch(function () {
        return false;
      });
  }

  function refreshAccessToken(refreshToken) {
    var body = new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: CLIENT_ID,
      refresh_token: refreshToken
    });
    return fetch(AUTH_BASE + '/oauth/token', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString()
    })
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .then(function (tokens) {
        if (!tokens || !tokens.access_token) return null;
        saveTokens(tokens);
        return tokens.access_token;
      })
      .catch(function () {
        return null;
      });
  }

  function getValidAccessToken() {
    var tokens = getTokens();
    if (!tokens) return Promise.resolve(null);

    var ageSeconds = (Date.now() - (tokens.obtained_at || 0)) / 1000;
    var stillValid = tokens.expires_in && ageSeconds < tokens.expires_in - 60;

    if (stillValid) return Promise.resolve(tokens.access_token);
    if (!tokens.refresh_token) return Promise.resolve(null);
    return refreshAccessToken(tokens.refresh_token);
  }

  // ---- Customer Account API calls -----------------------------------------
  function apiRequest(accessToken, query, variables) {
    return fetch(API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: accessToken
      },
      body: JSON.stringify({ query: query, variables: variables || {} })
    })
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .catch(function () {
        return null;
      });
  }

  function fetchServerWishlist(accessToken) {
    var query =
      'query GetWishlist { customer { id metafield(namespace: "' +
      METAFIELD_NAMESPACE +
      '", key: "' +
      METAFIELD_KEY +
      '") { value } } }';
    return apiRequest(accessToken, query).then(function (result) {
      if (!result || !result.data || !result.data.customer) return { customerId: null, handles: [] };
      var customerId = result.data.customer.id;
      var raw = result.data.customer.metafield && result.data.customer.metafield.value;
      var handles = [];
      if (raw) {
        try {
          handles = JSON.parse(raw);
        } catch (e) {}
      }
      return { customerId: customerId, handles: Array.isArray(handles) ? handles : [] };
    });
  }

  function pushServerWishlist(accessToken, customerId, handles) {
    var mutation =
      'mutation SetWishlist($metafields: [MetafieldsSetInput!]!) {' +
      ' metafieldsSet(metafields: $metafields) {' +
      '   metafields { id }' +
      '   userErrors { field message }' +
      ' }' +
      '}';
    var variables = {
      metafields: [
        {
          ownerId: customerId,
          namespace: METAFIELD_NAMESPACE,
          key: METAFIELD_KEY,
          type: 'json',
          value: JSON.stringify(handles)
        }
      ]
    };
    return apiRequest(accessToken, mutation, variables);
  }

  // ---- Local wishlist bridge (matches wishlist.js's storage format) -------
  function localStorageKey() {
    var customerId = document.body ? document.body.getAttribute('data-customer-id') : '';
    return customerId ? 'bf_wishlist_customer_' + customerId : 'bf_wishlist';
  }

  function getLocalWishlist() {
    try {
      var raw = localStorage.getItem(localStorageKey());
      return raw ? JSON.parse(raw) : [];
    } catch (e) {
      return [];
    }
  }

  function saveLocalWishlist(handles) {
    try {
      localStorage.setItem(localStorageKey(), JSON.stringify(handles));
    } catch (e) {}
  }

  function mergeHandles(a, b) {
    var set = {};
    a.concat(b).forEach(function (h) {
      if (h) set[h] = true;
    });
    return Object.keys(set);
  }

  // ---- Main sync routine ---------------------------------------------------
  function syncWishlist() {
    return getValidAccessToken().then(function (accessToken) {
      if (!accessToken) return;
      return fetchServerWishlist(accessToken).then(function (server) {
        if (!server.customerId) return;
        var local = getLocalWishlist();
        var merged = mergeHandles(local, server.handles);

        // Update local storage + on-screen state so the UI reflects the merge
        saveLocalWishlist(merged);
        if (window.toggleWishlist && document.querySelectorAll) {
          document.querySelectorAll('[data-wishlist-handle]').forEach(function (node) {
            var handle = node.getAttribute('data-wishlist-handle');
            var active = merged.indexOf(handle) !== -1;
            node.classList.toggle('is-active', active);
          });
        }
        if (document.querySelector('.js-wishlistBlock') && typeof window.renderWishlistPage === 'function') {
          window.renderWishlistPage();
        }

        // Push the merged list back up if it differs from what the server had
        var changed =
          merged.length !== server.handles.length ||
          merged.some(function (h) {
            return server.handles.indexOf(h) === -1;
          });
        if (changed) {
          pushServerWishlist(accessToken, server.customerId, merged);
        }
      });
    });
  }

  function init() {
    if (!isConfigured()) return;
    handleRedirectCallback().then(function () {
      syncWishlist();
    });
  }

  init();
  document.addEventListener('DOMContentLoaded', init);
})();
